/* =============================================================================
 * Pill / Badge — APS primitive (Track D)
 * Small status/label chip. Tones carry the fixed semantics: success = earned/
 * positive (Opened/Replied/Unlocked), credit = the orange "N free intros" cue,
 * neutral = informational, warning = BILLING ONLY, danger = destructive/error.
 * ========================================================================== */
import React from 'react';
import { radius, font } from '../../styles/tokens';

export type PillTone = 'success' | 'credit' | 'neutral' | 'warning' | 'danger';

export interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: PillTone;
  icon?: React.ReactNode;
}

const TONES: Record<PillTone, { bg: string; fg: string }> = {
  success: { bg: 'var(--aps-success-tint)', fg: 'var(--aps-success-text)' },
  credit: { bg: 'var(--aps-action-tint-strong)', fg: 'var(--aps-action-on-tint)' },
  neutral: { bg: 'var(--aps-fill-2)', fg: 'var(--aps-text-2)' },
  warning: { bg: 'var(--aps-warning-tint)', fg: 'var(--aps-warning-text)' }, // BILLING ONLY
  danger: { bg: 'var(--aps-danger-tint)', fg: 'var(--aps-danger)' },
};

export const Pill: React.FC<PillProps> = ({ tone = 'neutral', icon, style, children, ...rest }) => {
  const t = TONES[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: t.bg,
        color: t.fg,
        fontFamily: font.sans,
        fontSize: 12,
        fontWeight: font.weight.bold,
        padding: '5px 10px',
        borderRadius: radius.pill,
        lineHeight: 1.1,
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...rest}
    >
      {icon}
      {children}
    </span>
  );
};

export default Pill;
